import { computed } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type {
  GetLegacyServicePickersResponse,
  GetPickersResponse,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import type { CondensedStep } from '~/scopes/product-core/components/presentationals/CustomizationFunnelCondensed/CustomizationFunnelCondensed.types'
import { usePickerColConfig } from '~/scopes/product-core/components/presentationals/CustomizationFunnelCondensed/composables/usePickerColConfig/usePickerColConfig'
import { createCondensedFunnelData } from '~/scopes/product-core/components/presentationals/CustomizationFunnelCondensed/utils/createCondensedFunnelData'

export type UseCondensedFunnelDataArgs = {
  pickers: GetPickersResponse
  pickersServices: GetLegacyServicePickersResponse['pickerGroups']
  product: GetProductResponse
  price: Price | null | undefined
  discountedPrice: Price
}

/**
 * Generates the condensed funnel config (the list of rows) from the pickers
 * API responses. This is the condensed counterpart of what
 * `createCustomizationFunnelData` feeds to the classic funnel.
 *
 * It is the only Vue-context caller of `usePickerColConfig` — the pure
 * factory receives `getPickerDisplay` as a plain function.
 */
export function useCondensedFunnelData(
  getData: () => UseCondensedFunnelDataArgs,
) {
  const i18n = useI18n()
  const experiments = useExperiments()
  // The product (and so its category) is stable for the lifetime of the page,
  // the col config can be resolved once from the initial data.
  const { getPickerDisplay } = usePickerColConfig(
    getData().product.tracking.categoryId,
  )

  const steps = computed<CondensedStep[]>(() => {
    const { pickers, pickersServices, product, price, discountedPrice } =
      getData()

    return createCondensedFunnelData(
      i18n,
      pickers,
      pickersServices,
      product,
      price,
      discountedPrice,
      experiments,
      getPickerDisplay,
    )
  })

  return { steps }
}
